import { useState } from 'react';
import type { Field, FieldGroup, FormValues } from '../../api/types';
import { FieldControl } from './FieldControl';

interface FormSectionProps {
  group: FieldGroup;
  fields: Field[];
  values: FormValues;
  fieldErrors: Record<string, string[]>;

  /** Ghost text for a control a chosen option was meant to fill and left empty, by field key. */
  unfilledPlaceholders: Record<string, string>;

  onChange: (key: string, value: string) => void;
}

/**
 * One subsection of the form.
 *
 * Collapsible subsections use a plain button and a conditional render rather than
 * <c>&lt;details&gt;</c>, because an error inside a closed section has to be able to force it open —
 * which native disclosure does not offer without fighting it.
 */
export function FormSection({ group, fields, values, fieldErrors, unfilledPlaceholders, onChange }: FormSectionProps) {
  const [open, setOpen] = useState(!group.collapsible || group.defaultOpen);

  const errorCount = fields.reduce((total, field) => total + (fieldErrors[field.key]?.length ?? 0), 0);

  // A collapsed section hiding a validation error would leave the sender hunting for it.
  const expanded = open || errorCount > 0;

  const isTicked = (key: string) => values[key] === 'true';

  // A field the manifest gates behind a checkbox is not on the page until that box is ticked. Not
  // disabled — a clinic that runs straight through has no second shift, and a greyed-out pair of times
  // would read as one it had chosen to leave blank.
  const visible = fields.filter((field) => field.shownWhen === null || isTicked(field.shownWhen));

  return (
    <section className="subsection">
      {group.collapsible ? (
        <button
          type="button"
          className="subsection__toggle"
          aria-expanded={expanded}
          onClick={() => setOpen(!expanded)}
        >
          <span className={expanded ? 'subsection__chevron subsection__chevron--open' : 'subsection__chevron'} aria-hidden="true" />
          <span className="subsection__title">{group.label}</span>
          {errorCount > 0 && (
            <span className="subsection__badge">
              {errorCount === 1 ? '1 to fix' : `${errorCount} to fix`}
            </span>
          )}
        </button>
      ) : (
        <h3 className="subsection__title subsection__title--static">{group.label}</h3>
      )}

      {expanded && (
        <div
          className={group.columns === null ? 'subsection__fields' : 'subsection__fields subsection__fields--fixed'}
          /* A custom property rather than an inline grid-template, so the narrow-screen media query
             can still override it — an inline style would win over any stylesheet rule. */
          style={group.columns === null ? undefined : ({ '--cols': group.columns } as React.CSSProperties)}
        >
          {visible.map((field) => (
            <FieldControl
              key={field.key}
              field={field}
              value={values[field.key] ?? ''}
              errors={fieldErrors[field.key] ?? []}
              disabled={field.disabledWhen !== null && isTicked(field.disabledWhen)}
              placeholderOverride={unfilledPlaceholders[field.key] ?? null}
              /* While the fields it reveals are away, the checkbox takes the row they would have
                 filled. Otherwise the next day's controls flow into the gap and the whole point of a
                 fixed column count — one day per row — is lost. */
              fullRow={revealsHiddenFields(field, fields, values)}
              onChange={onChange}
            />
          ))}
        </div>
      )}
    </section>
  );
}

/** True for a checkbox that gates other fields in this subsection and is not ticked. */
function revealsHiddenFields(field: Field, siblings: Field[], values: FormValues): boolean {
  return (
    field.type === 'checkbox' &&
    values[field.key] !== 'true' &&
    siblings.some((sibling) => sibling.shownWhen === field.key)
  );
}
